import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import config from '../../../config';

// HR Shift master — name + start/end times + grace + OT/night flags.
// Shifts later get assigned to employees and drive attendance + payroll.
export default function Shifts() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    startTime: '09:00',
    endTime: '18:00',
    graceMinutes: 0,
    isOTApplicable: true,
    isNightShift: false,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Shifts/${companyId}?includeInactive=${includeInactive}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed');
      setRows(await res.json());
    } catch (e) {
      toast('Failed to load shifts', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, includeInactive]);

  const openAdd = () => {
    setEditing(null);
    setForm({
      name: '',
      startTime: '09:00',
      endTime: '18:00',
      graceMinutes: 0,
      isOTApplicable: true,
      isNightShift: false,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.shiftName || '',
      startTime: (row.startTime || '09:00:00').slice(0, 5),
      endTime: (row.endTime || '18:00:00').slice(0, 5),
      graceMinutes: row.graceMinutes || 0,
      isOTApplicable: !!row.isOTApplicable,
      isNightShift: !!row.isNightShift,
      isActive: !!row.isActive,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast('Please enter a shift name', 'warning');
      return;
    }
    if (!form.startTime || !form.endTime) {
      toast('Please set start and end times', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        Id: editing?.id || null,
        CompanyId: companyId,
        ShiftName: form.name.trim(),
        StartTime: form.startTime,
        EndTime: form.endTime,
        GraceMinutes: Number(form.graceMinutes) || 0,
        IsOTApplicable: form.isOTApplicable,
        IsNightShift: form.isNightShift,
        IsActive: form.isActive,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Shift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast(data.message || 'Save failed', 'error');
        return;
      }
      toast(editing ? 'Shift updated' : 'Shift added');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.shiftName}"?`)) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Shift/${row.id}/${companyId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error();
      toast('Shift deleted');
      load();
    } catch (e) {
      toast('Delete failed', 'error');
    }
  };

  // "HH:mm:ss" → "08:00 AM" for display
  const fmt = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':');
    const hh = parseInt(h, 10);
    const ap = hh >= 12 ? 'PM' : 'AM';
    const dh = hh % 12 === 0 ? 12 : hh % 12;
    return `${String(dh).padStart(2, '0')}:${m} ${ap}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent='space-between'
        sx={{ mb: 2 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#0ea5e9',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <AccessTimeIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Shifts
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR / Shift master · {rows.length} record(s)
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1.5}>
          <Stack direction='row' alignItems='center'>
            <Switch
              size='small'
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
              Show inactive
            </Typography>
          </Stack>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
            Add Shift
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 6px rgba(15,23,42,0.06)' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Shift</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Start</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>End</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Grace (min)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No shifts found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((r, i) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {r.shiftName}
                      </TableCell>
                      <TableCell>{fmt(r.startTime)}</TableCell>
                      <TableCell>{fmt(r.endTime)}</TableCell>
                      <TableCell>{r.graceMinutes}</TableCell>
                      <TableCell>
                        <Stack direction='row' spacing={0.5}>
                          {r.isOTApplicable && (
                            <Chip size='small' label='OT' sx={{ bgcolor: '#e0f2fe', color: '#0369a1' }} />
                          )}
                          {r.isNightShift && (
                            <Chip size='small' label='Night' sx={{ bgcolor: '#fef3c7', color: '#92400e' }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          label={r.isActive ? 'Active' : 'Inactive'}
                          sx={{
                            bgcolor: r.isActive ? '#dcfce7' : '#fee2e2',
                            color: r.isActive ? '#166534' : '#991b1b',
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => openEdit(r)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton
                            size='small'
                            onClick={() => remove(r)}
                            sx={{ color: '#dc2626' }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit Shift' : 'New Shift'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label='Shift Name *'
                size='small'
                fullWidth
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Start Time *'
                type='time'
                size='small'
                fullWidth
                value={form.startTime}
                InputLabelProps={{ shrink: true }}
                onChange={(e) =>
                  setForm({ ...form, startTime: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='End Time *'
                type='time'
                size='small'
                fullWidth
                value={form.endTime}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Grace Minutes'
                type='number'
                size='small'
                fullWidth
                value={form.graceMinutes}
                inputProps={{ min: 0 }}
                onChange={(e) =>
                  setForm({ ...form, graceMinutes: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Switch
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm({ ...form, isActive: e.target.checked })
                  }
                />
                <Typography sx={{ fontSize: '0.85rem' }}>
                  {form.isActive ? 'Active' : 'Inactive'}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Switch
                  checked={form.isOTApplicable}
                  onChange={(e) =>
                    setForm({ ...form, isOTApplicable: e.target.checked })
                  }
                />
                <Typography sx={{ fontSize: '0.85rem' }}>
                  Overtime Applicable
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Switch
                  checked={form.isNightShift}
                  onChange={(e) =>
                    setForm({ ...form, isNightShift: e.target.checked })
                  }
                />
                <Typography sx={{ fontSize: '0.85rem' }}>
                  Night Shift
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
